import React, { useEffect, useState } from "react";
import "./Addresses.scss";
import Cookies from "js-cookie";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { notifyError } from "../../utils/notify";
import { EMAIL_MAX_LENGTH, isValidEmail, isValidPhone } from "../../utils/validators";


import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Grid, MenuItem, Select, TextField, FormControl
} from "@mui/material";
const validateName = (value) => /^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+$/.test(value);
const validateNIP = (value) => /^\d{10}$/.test(value);
const postalCodePattern = /^\d{2}-\d{3}$/;
const validatePostalCode = (value) => postalCodePattern.test(value);
const countryOptions = [
    { name: "Afghanistan", code: "AF", dialingCode: "+93" },
    { name: "Albania", code: "AL", dialingCode: "+355" },
    { name: "Algeria", code: "DZ", dialingCode: "+213" },
    { name: "Andorra", code: "AD", dialingCode: "+376" },
    { name: "Angola", code: "AO", dialingCode: "+244" },
    { name: "Polska", code: "PL", dialingCode: "+48" }
];
const defaultCountry = "Polska";
const API_URL = process.env.REACT_APP_API_URL;

/**
 * Get all addresses for the authenticated user
 */
const getUserAddresses = async () => {
    const token = Cookies.get("token");

    if (!token) {
        throw new Error("Authentication required");
    }

    const response = await fetch(`${API_URL}/addresses/me`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || "Unable to fetch addresses");
    }

    return response.json();
};

/**
 * Create a new address
 */
const saveAddress = async (addressData) => {
    const token = Cookies.get("token");

    if (!token) {
        throw new Error("Please log in to save an address.");
    }

    const response = await fetch(`${API_URL}/addresses`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: addressData }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || "Failed to save address");
    }

    return response.json();
};

/**
 * Update an existing address
 */
const updateAddress = async (documentId, addressData) => {
    const token = Cookies.get("token");

    if (!token) {
        throw new Error("Authentication required");
    }

    const response = await fetch(`${API_URL}/addresses/${documentId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ data: addressData }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || "Unable to update address");
    }

    return response.json();
};

/**
 * Delete an address
 */
const deleteAddress = async (documentId) => {
    const token = Cookies.get("token");

    if (!token) {
        throw new Error("Authentication required");
    }

    const response = await fetch(`${API_URL}/addresses/${documentId}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        }
    });

    if (!response.ok && response.status !== 204) {
        const error = await response.json();
        throw new Error(error.error?.message || "Unable to delete address");
    }

    return null;
};

/**
 * Set an address as default
 */
const setDefaultAddress = async (documentId) => {
    const token = Cookies.get("token");

    if (!token) {
        throw new Error("Authentication required");
    }

    const response = await fetch(`${API_URL}/addresses/${documentId}/set-default`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || "Unable to set default address");
    }

    return response.json();
};


const Addresses = ({ open, onClose, onSave, initialData }) => {
    const intl = useIntl();
    const [form, setForm] = useState({
        isDefault: false,
        country: defaultCountry,
        firstName: "",
        lastName: "",
        nip: "",
        company: "",
        address: "",
        zip: "",
        city: "",
        phone: "+48",
        email: "",
        isCompany: false,
    });

    useEffect(() => {
        if (open) {
            if (initialData) {
                setForm({
                    isDefault: initialData.isDefault || false,
                    country: initialData.country || defaultCountry,
                    firstName: initialData.firstName || "",
                    lastName: initialData.lastName || "",
                    nip: initialData.nip || "",
                    company: initialData.company || "",
                    address: initialData.address || "",
                    zip: initialData.zip || "",
                    city: initialData.city || "",
                    // Stored as digits (biginteger); the form works with "+48...".
                    phone: initialData.phone ? `+${String(initialData.phone).replace(/^\+/, "")}` : "+48",
                    email: initialData.email || "",
                    isCompany: !!(initialData.nip || initialData.company),
                });
            } else {
                setForm({
                    isDefault: false,
                    country: defaultCountry,
                    firstName: "",
                    lastName: "",
                    nip: "",
                    company: "",
                    address: "",
                    zip: "",
                    city: "",
                    phone: "+48",
                    email: "",
                    isCompany: false,
                });
            }
            setErrors({});
        }
    }, [initialData, open]);


    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        let { name, value, type, checked } = e.target;
        if (type === "checkbox") {
            setForm((prev) => ({
                ...prev,
                [name]: checked,
                ...(name === "isCompany" && !checked ? { nip: "", company: "" } : {})
            }));
            setErrors((prev) => ({
                ...prev,
                ...(name === "isCompany" && !checked ? { nip: undefined, company: undefined } : {})
            }));
        } else {
            if (name === "zip") {
                value = value.replace(/[^\d]/g, "");
                if (value.length > 2) value = `${value.slice(0, 2)}-${value.slice(2, 5)}`;
                if (value.length > 6) value = value.slice(0, 6);
            }
            if (name === "phone") {
                value = value.replace(/[^\d]/g, "");
                if (!value.startsWith("48")) value = "48" + value;
                if (value.length > 11) value = value.slice(0, 11);
                value = `+${value}`;
            }
            if (name === "nip") {
                value = value.replace(/[^\d]/g, "");
                if (value.length > 10) value = value.slice(0, 10);
            }
            if (["firstName", "lastName"].includes(name)) {
                value = value.replace(/[^A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]/g, "");
            }
            if (name === "email") value = value.replace(/\s/g, "").slice(0, EMAIL_MAX_LENGTH);
            setForm((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = () => {
        let errs = {};
        if (!validateName(form.firstName)) errs.firstName = "Imię może zawierać tylko litery.";
        if (!validateName(form.lastName)) errs.lastName = "Nazwisko może zawierać tylko litery.";
        if (form.isCompany) {
            if (!form.company.trim()) errs.company = "Nazwa firmy jest wymagana.";
            if (!validateNIP(form.nip)) errs.nip = "NIP musi mieć dokładnie 10 cyfr.";
        }
        if (!validatePostalCode(form.zip)) errs.zip = "Kod pocztowy w formacie 00-000.";
        // Same per-country phone check as checkout (libphonenumber).
        if (!isValidPhone(form.phone)) errs.phone = "Nieprawidłowy numer telefonu.";
        if (form.email && !isValidEmail(form.email)) errs.email = "Podaj poprawny adres e-mail, np. jan@example.pl.";
        if (!form.address.trim()) errs.address = "Adres jest wymagany.";
        if (!form.city.trim()) errs.city = "Miasto jest wymagane.";
        return errs;
    };

    // Same city/postal-code rule as checkout and POST /orders. Network errors don't block.
    const cityError = async () => {
        try {
            const res = await fetch(`${API_URL}/postal-codes/${form.zip}?city=${encodeURIComponent(form.city.trim())}`);
            const json = res.ok ? await res.json() : null;
            const code = json?.data?.cityError;
            if (code === "city_mismatch") return intl.formatMessage({ id: "error_city_mismatch" }, { zip: form.zip });
            if (code === "unknown_place") return intl.formatMessage({ id: "error_city_unknown" });
            return null;
        } catch {
            return null;
        }
    };

    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        const errs = validateForm();
        if (!errs.zip && !errs.city) {
            const cityErr = await cityError();
            if (cityErr) errs.city = cityErr;
        }
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        try {
            setSaving(true);
            // Strip the leading "+" before sending: the backend "phone" field is
            // stored as a biginteger and rejects a value containing "+".
            const payload = { ...form, phone: form.phone.replace(/^\+/, "") };
            if (onSave) await onSave(payload);
            onClose();
        } catch (err) {
            notifyError(err.message || intl.formatMessage({ id: "address_save_failed" }));
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
            sx: { borderRadius: 0, p: 3, bgcolor: "var(--mui-palette-background-paper)" }
        }}>
            <DialogTitle sx={{ fontWeight: 500, color: "var(--mui-palette-about-primary)" }}>
                {initialData ? "Edytuj adres" : "Dodaj adres"}
            </DialogTitle>
            <DialogContent sx={{ bgcolor: "background.default", borderRadius: 0, p: 3 }}>
                <FormControlLabel
                    control={<Checkbox name="isDefault" checked={form.isDefault} onChange={handleChange} />}
                    label="To jest mój adres domyślny"
                    sx={{ mb: 2 }}
                />
                <FormControlLabel
                    control={<Checkbox name="isCompany" checked={form.isCompany} onChange={handleChange} color="primary" />}
                    label="Firma (faktura VAT)?"
                    sx={{ mb: 3 }}
                />
                <Grid container spacing={4}>
                    <FormControl fullWidth sx={{ minWidth: 200, maxWidth: 400, fontSize: "0.9rem" }}>
                        <Select sx={{ height: 40, fontSize: "0.9rem" }}
                            label="Kraj/region"
                            name="country"
                            value={form.country}
                            onChange={handleChange}
                            fullWidth
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 380,
                                        width: 350,
                                        lineHeight: "1.5em",
                                    },
                                },
                            }}
                        >
                            {countryOptions.map(opt => (
                                <MenuItem key={opt.code} value={opt.name}>
                                    {opt.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Grid item xs={12} sm={6}>
                        <TextField label="E-mail" name="email" value={form.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} fullWidth autoComplete="email" inputProps={{ maxLength: EMAIL_MAX_LENGTH, inputMode: "email" }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Imię" name="firstName" value={form.firstName} onChange={handleChange} error={!!errors.firstName} helperText={errors.firstName} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Nazwisko" name="lastName" value={form.lastName} onChange={handleChange} error={!!errors.lastName} helperText={errors.lastName} fullWidth />
                    </Grid>
                    {form.isCompany && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField label="NIP" name="nip" value={form.nip} onChange={handleChange} error={!!errors.nip} helperText={errors.nip} fullWidth inputProps={{ maxLength: 10 }} required />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Firma" name="company" value={form.company} onChange={handleChange} error={!!errors.company} helperText={errors.company} fullWidth required inputProps={{ maxLength: 200 }} />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <TextField label="Adres" name="address" value={form.address} onChange={handleChange} error={!!errors.address} helperText={errors.address} fullWidth inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Kod pocztowy" name="zip" value={form.zip} onChange={handleChange} error={!!errors.zip} helperText={errors.zip} fullWidth inputProps={{ maxLength: 6 }} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Miasto" name="city" value={form.city} onChange={handleChange} error={!!errors.city} helperText={errors.city} fullWidth inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Telefon" name="phone" value={form.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone} fullWidth inputProps={{ maxLength: 12 }} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: "var(--mui-palette-background-paper)" }}>
                <Button variant="outlined" color="error" onClick={onClose} disabled={saving}>Anuluj</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        background: "linear-gradient(90deg, var(--mui-palette-about-gradientStart), var(--mui-palette-about-gradientEnd))",
                        color: "#fff",
                        "&:hover": { opacity: 0.9 },
                    }}
                >
                    {saving ? "Zapisywanie…" : "Zapisz"}
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export {
    getUserAddresses,
    saveAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
};
export default Addresses;
